import { useEffect, useState } from "react";
import api, { errorMessage } from "@/lib/api";
import { toast } from "sonner";
import { CalendarClock, RefreshCw, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

export default function AssignmentHeatmap() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drill, setDrill] = useState(null); // {staff_name, date, leads[], count}
  const [drillLoading, setDrillLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/reports/assignment-heatmap?days=${days}`);
      setData(r.data);
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const deltaBadge = (delta) => {
    if (delta === 0 || delta === undefined) return <span className="text-slate-500 text-[10px]">·</span>;
    const up = delta > 0;
    return (
      <span className={`text-[10px] font-bold ${up ? "text-emerald-400" : "text-red-400"}`} title="vs previous period">
        {up ? "▲" : "▼"} {Math.abs(delta)}
      </span>
    );
  };

  const openCell = async (staffId, date, cellValue) => {
    if (!cellValue) return; // don't drill on empty
    setDrillLoading(true);
    try {
      const r = await api.get(`/admin/reports/heatmap-cell?staff_id=${staffId}&date=${date}`);
      setDrill(r.data);
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setDrillLoading(false); }
  };

  const shade = (v) => {
    if (!data || data.max === 0 || !v) return "bg-slate-900/60 text-slate-600 border-white/5";
    const pct = v / data.max;
    if (pct >= 0.75) return "bg-amber-500 text-slate-900 border-amber-400";
    if (pct >= 0.5) return "bg-amber-500/70 text-slate-900 border-amber-400/60";
    if (pct >= 0.25) return "bg-amber-500/40 text-amber-100 border-amber-500/40";
    return "bg-amber-500/15 text-amber-300 border-amber-500/20";
  };

  const dayLabel = (k) => {
    try { const d = new Date(k + "T00:00:00"); return d.toLocaleDateString("en-IN", { weekday: "short", day: "2-digit" }); }
    catch { return k; }
  };

  const top = (data?.rows || []).slice(0, 10);

  return (
    <div className="card-surface p-6" data-testid="assignment-heatmap">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center justify-center"><CalendarClock className="w-5 h-5 text-amber-500"/></div>
          <div>
            <div className="text-xs uppercase tracking-widest text-amber-500 font-bold">Assignment Heatmap</div>
            <div className="font-heading text-lg text-white">Last {days} days · Top 10 telecallers</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-full border border-white/10 p-1 bg-slate-950/60" data-testid="heatmap-range-toggle">
            {[7, 14, 30].map(n => (
              <button
                key={n}
                type="button"
                onClick={() => setDays(n)}
                data-testid={`heatmap-range-${n}`}
                className={`text-[11px] uppercase tracking-widest px-3 py-1 rounded-full transition-colors ${days === n ? "bg-amber-500 text-slate-900 font-bold" : "text-slate-400 hover:text-white"}`}
              >{n}d</button>
            ))}
          </div>
          <button onClick={load} disabled={loading} data-testid="heatmap-refresh-btn" className="text-[11px] uppercase tracking-widest text-slate-500 hover:text-amber-400 flex items-center gap-1 disabled:opacity-50">
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`}/> Refresh
          </button>
        </div>
      </div>

      {!data && <div className="text-sm text-slate-500 p-6 text-center">Loading…</div>}

      {data && top.length === 0 && (
        <div className="text-sm text-slate-500 p-6 text-center">No assignments yet in the last {days} days.</div>
      )}

      {data && top.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr>
                <th className="text-left text-[10px] uppercase tracking-widest text-slate-500 pb-2 pr-3 sticky left-0 bg-slate-950">Telecaller</th>
                {data.days.map(d => (
                  <th key={d} className="text-center text-[10px] uppercase tracking-widest text-slate-500 pb-2 px-1">{dayLabel(d)}</th>
                ))}
                <th className="text-right text-[10px] uppercase tracking-widest text-slate-500 pb-2 pl-3">Total</th>
              </tr>
            </thead>
            <tbody>
              {top.map(r => (
                <tr key={r.id} data-testid={`heatmap-row-${r.id}`} className="border-t border-white/5">
                  <td className="py-1.5 pr-3 text-white text-sm sticky left-0 bg-slate-950">
                    <div className="truncate max-w-[140px]">{r.name}</div>
                    <div className="text-[10px] text-slate-500 font-mono-alt">{r.phone}</div>
                  </td>
                  {r.cells.map((c, i) => (
                    <td key={i} className="px-1 py-1.5 text-center">
                      <button
                        type="button"
                        disabled={!c}
                        onClick={() => openCell(r.id, data.days[i], c)}
                        data-testid={`heatmap-cell-${r.id}-${data.days[i]}`}
                        title={c ? `Click to view ${c} lead${c === 1 ? "" : "s"}` : "No leads"}
                        className={`w-10 h-8 rounded-md border flex items-center justify-center font-mono-alt font-bold transition-transform ${shade(c)} ${c ? "cursor-pointer hover:scale-110" : "cursor-default"}`}
                      >
                        {c || ""}
                      </button>
                    </td>
                  ))}
                  <td className="py-1.5 pl-3 text-right text-sm">
                    <div className="font-mono-alt text-amber-400 leading-none">{r.total}</div>
                    <div className="mt-0.5">{deltaBadge(r.delta)}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-4 text-[11px] text-slate-500 flex items-center gap-3 flex-wrap">
            <span>Total assigned in period: <span className="text-white font-bold">{data.total}</span></span>
            <span className="pl-1">vs previous {days}d: <span className="text-slate-300 font-mono-alt">{data.prev_total ?? 0}</span> {deltaBadge(data.delta)}</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-500/15 border border-amber-500/20"/>low</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-500/40 border border-amber-500/40"/>mid</span>
            <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-500 border border-amber-400"/>peak</span>
            <span className="text-slate-600">· Tap a cell to see the leads</span>
          </div>
        </div>
      )}

      {/* Drill-down dialog */}
      <Dialog open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <DialogContent className="bg-slate-900 border-white/10 text-white max-w-2xl" data-testid="heatmap-drill-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-amber-400">
              {drill?.staff_name} · {drill?.date} · {drill?.count} lead{drill?.count === 1 ? "" : "s"}
            </DialogTitle>
          </DialogHeader>
          <div className="max-h-[420px] overflow-y-auto rounded-lg border border-white/5 divide-y divide-white/5" data-testid="heatmap-drill-list">
            {drillLoading && <div className="p-6 text-center text-slate-500 text-sm">Loading…</div>}
            {!drillLoading && (drill?.leads || []).map(l => (
              <div key={l.lead_id} className="px-4 py-3 flex items-center gap-3 hover:bg-white/5" data-testid={`drill-lead-${l.lead_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="font-mono-alt text-amber-400 text-xs">{l.lead_id}</span>
                    <span className="text-white text-sm truncate">{l.name}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 font-mono-alt">
                    {l.phone} · {l.loan_type || "—"} {l.amount ? `· ₹${Number(l.amount).toLocaleString("en-IN")}` : ""}
                  </div>
                </div>
                <Badge className="uppercase text-[10px]">{l.status}</Badge>
              </div>
            ))}
            {!drillLoading && (!drill?.leads || drill.leads.length === 0) && (
              <div className="p-6 text-center text-slate-500 text-sm">No leads found for this day.</div>
            )}
          </div>
          <div className="flex justify-end mt-4">
            <Button data-testid="heatmap-drill-close" onClick={() => setDrill(null)} variant="ghost" className="text-slate-300 hover:text-white">
              <X className="w-4 h-4 mr-2"/> Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

